import { useEffect, useState } from 'react';
import { Checkbox, Input, Modal, Table, Tag, Typography } from 'antd';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { useGlobalContext } from '@contexts/global';
import { TransferLocationSearchHook } from '@hooks/location';
import type { InventorySuggestedTransferItem } from 'schemas/inventorySuggestedTransfers';
import type { TransferLocationSearchRow } from 'schemas/transferLocationSearch';
import { InventoryService, ToastMessage } from '@utility/index';

const { Text } = Typography;

type DestinationSuggestionModalProps = {
  open: boolean;
  onClose: () => void;
  item?: InventorySuggestedTransferItem | null;
  refresh: () => void;
};

function CapabilityItem({ label, allowed }: { label: string; allowed: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        whiteSpace: 'nowrap',
        fontSize: 12,
        minWidth: 0,
      }}
    >
      <Text type="secondary">{label}</Text>
      {allowed ? (
        <CheckCircle color="#22c55e" weight="fill" size={16} />
      ) : (
        <XCircle color="#ef4444" weight="fill" size={16} />
      )}
    </div>
  );
}

function CapabilitiesCell({
  capabilities,
}: {
  capabilities: TransferLocationSearchRow['capabilities'];
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(140px, 1fr))',
        gap: '8px 32px',
      }}
    >
      <CapabilityItem label="Mixed SKU" allowed={capabilities.allowsMixedProducts} />
      <CapabilityItem label="Mixed lot" allowed={capabilities.allowsMixedLots} />
      <CapabilityItem label="Picking" allowed={capabilities.isPickingLocation} />
      <CapabilityItem label="Stackable" allowed={capabilities.stackable} />
    </div>
  );
}

export function DestinationSuggestionModal({
  open,
  onClose,
  item,
  refresh,
}: DestinationSuggestionModalProps) {
  const { warehouseId } = useGlobalContext();
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const isEditingDestination = !!item?.destinationLocationId;

  const { data, loading, filterChanged, filter, totalData, pageChanged } =
    TransferLocationSearchHook(warehouseId);

  useEffect(() => {
    if (open) {
      setSelectedLocationId(item?.destinationLocationId?._id ?? null);
    }
  }, [item?.destinationLocationId?._id, open]);

  const handleClose = () => {
    if (confirmLoading) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedLocationId || !item?._id) return;

    if (isEditingDestination && selectedLocationId === item.destinationLocationId?._id) {
      onClose();
      return;
    }

    setConfirmLoading(true);
    try {
      await InventoryService.updateSuggestedTransfer(item._id, {
        destinationLocationId: selectedLocationId,
        suggestedQuantity: item.suggestedQuantity,
      });
      ToastMessage.success('Destination confirmed successfully');
      refresh();
      onClose();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const columns = [
    {
      title: 'Location',
      key: 'code',
      dataIndex: 'code',
      width: 130,
      ellipsis: true,
    },
    {
      title: 'Zone & Rack',
      key: 'zoneRack',
      width: 180,
      render: (_: unknown, row: TransferLocationSearchRow) => (
        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
          <Tag color={row.zone.color}>{row.zone.code}</Tag>
          <Tag color={row.zone.color}>{row.rack.code}</Tag>
        </div>
      ),
    },
    {
      title: 'Capabilities',
      key: 'capabilities',
      width: 380,
      render: (_: unknown, row: TransferLocationSearchRow) => (
        <CapabilitiesCell capabilities={row.capabilities} />
      ),
    },
  ];

  const pagination = {
    current: filter.page,
    pageSize: filter.limit,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: [10, 25, 50],
    showTotal: (total: number) => `${total} locations`,
    onChange: (page: number, pageSize: number) => {
      if (pageSize !== filter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };

  return (
    <Modal
      title={isEditingDestination ? 'Edit Destination' : 'Suggest Destination'}
      open={open}
      centered
      width={1024}
      onCancel={handleClose}
      onOk={handleConfirm}
      okText={isEditingDestination ? 'Update Destination' : 'Confirm Destination'}
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: !selectedLocationId }}
      cancelButtonProps={{ disabled: confirmLoading }}
      destroyOnHidden
    >
      <div className="mb-3">
        <Text type="secondary">SKU</Text>
        <Text strong className="ml-2">
          {item?.productId?.p_sku || 'N/A'}
        </Text>
        <Text type="secondary" className="ml-3">
          Lot
        </Text>
        <Text strong className="ml-2">
          {item?.inventoryId?.lotNumber || 'N/A'}
        </Text>
        <Text type="secondary" className="ml-3">
          Suggested Quantity
        </Text>
        <Text strong className="ml-2">
          {item?.suggestedQuantity ?? 'N/A'}
        </Text>
      </div>

      <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
        <Input.Search
          allowClear
          placeholder="Search locations..."
          value={filter.search}
          onChange={event => filterChanged('search', event.target.value)}
          style={{ flex: 1 }}
        />
        <Checkbox
          checked={filter.isPickingLocation === undefined}
          onChange={event =>
            filterChanged('isPickingLocation', event.target.checked ? undefined : true)
          }
        >
          Show non-pickable locations as well
        </Checkbox>
      </div>

      <Table
        rowKey="_id"
        columns={columns}
        dataSource={data}
        loading={loading}
        size="middle"
        tableLayout="fixed"
        pagination={pagination}
        scroll={{ y: '50vh' }}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: selectedLocationId ? [selectedLocationId] : [],
          onChange: selectedKeys => {
            setSelectedLocationId(
              selectedKeys.length > 0 ? String(selectedKeys[0]) : null,
            );
          },
        }}
        onRow={row => ({
          onClick: () => setSelectedLocationId(row._id),
          style: { cursor: 'pointer' },
        })}
      />
    </Modal>
  );
}
